import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Shield, Plus, Trash2, AlertTriangle, CheckCircle, Clock, FileCheck, Award, Loader2, LogOut } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CREDENTIAL_TYPES = [
  'Registered Nurse (RN) License', 'Licensed Practical Nurse (LPN)', 'Certified Nursing Assistant (CNA)',
  'BLS Certification', 'ACLS Certification', 'PALS Certification', 'NPI Number', 'DEA Registration',
  'State Medical License', 'Physical Therapy License', 'Occupational Therapy License',
  'Respiratory Therapist License', 'Pharmacy License', 'CPR Certification', 'TB Test',
  'Background Check', 'Drug Screening', 'Other',
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

const StatusBadge = ({ status }) => {
  const config = {
    'Valid': { icon: CheckCircle, classes: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
    'Expiring Soon': { icon: Clock, classes: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
    'Expired': { icon: AlertTriangle, classes: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
  };
  const c = config[status] || config['Valid'];
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${c.classes}`}>
      <Icon className="h-3.5 w-3.5" />{status}
    </span>
  );
};

export const CredentialTrackerPage = () => {
  usePageMeta({
    title: 'Credential Tracker - Manage Your Healthcare Certifications',
    description: 'Track and manage your healthcare credentials, licenses, and certifications. Get expiration alerts and stay compliant with Digitive Health Credential Tracker.',
    path: '/tools/credentials',
  });

  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ credential_type: '', credential_number: '', issuing_state: '', expiration_date: '' });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate('/auth?redirect=/tools/credentials');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) fetchCredentials();
  }, [user]);

  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/credentials`, { credentials: 'include' });
      if (res.ok) setCredentials(await res.json());
      else if (res.status === 401) navigate('/auth?redirect=/tools/credentials');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.credential_type || !form.expiration_date) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/credentials`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setForm({ credential_type: '', credential_number: '', issuing_state: '', expiration_date: '' });
        setShowForm(false);
        await fetchCredentials();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_URL}/api/credentials/${id}`, { method: 'DELETE', credentials: 'include' });
      await fetchCredentials();
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>;
  }

  if (!user) return null;

  const stats = {
    total: credentials.length,
    valid: credentials.filter(c => c.status === 'Valid').length,
    expiring: credentials.filter(c => c.status === 'Expiring Soon').length,
    expired: credentials.filter(c => c.status === 'Expired').length,
  };

  return (
    <div className="min-h-screen bg-background" data-testid="credential-tracker-page">
      <Header />
      <section className="pt-28 pb-14 bg-secondary relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }} />
        <div className="container-width relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4 text-sm font-medium">
                <Shield className="h-4 w-4" />Compliance Management
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-2">Credential Tracker</h1>
              <p className="text-sm text-muted-foreground">Welcome back, <span className="font-medium text-foreground">{user.name}</span></p>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-muted-foreground hover:text-foreground" data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container-width max-w-4xl">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="credential-stats">
            <Card className="p-4 text-center border-border/50">
              <p className="text-3xl font-bold text-foreground tracking-tight">{stats.total}</p>
              <p className="text-xs text-muted-foreground mt-1 font-medium uppercase tracking-wider">Total</p>
            </Card>
            <Card className="p-4 text-center border-emerald-500/20 bg-emerald-500/5">
              <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 tracking-tight">{stats.valid}</p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 font-medium uppercase tracking-wider">Active</p>
            </Card>
            <Card className="p-4 text-center border-amber-500/20 bg-amber-500/5">
              <p className="text-3xl font-bold text-amber-600 dark:text-amber-400 tracking-tight">{stats.expiring}</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium uppercase tracking-wider">Expiring</p>
            </Card>
            <Card className="p-4 text-center border-red-500/20 bg-red-500/5">
              <p className="text-3xl font-bold text-red-600 dark:text-red-400 tracking-tight">{stats.expired}</p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium uppercase tracking-wider">Expired</p>
            </Card>
          </div>

          {/* Action Bar */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground tracking-tight">Your Credentials</h2>
            <Button onClick={() => setShowForm(!showForm)} className="bg-primary text-white rounded-full hover:translate-y-[-1px] transition-transform" data-testid="add-credential-btn">
              <Plus className="h-4 w-4 mr-2" /> Add New
            </Button>
          </div>

          {/* Add Form */}
          {showForm && (
            <Card className="p-6 mb-6 border-primary/20" data-testid="add-credential-form">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><Award className="h-4 w-4 text-primary" /> New Credential</h3>
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Credential Type *</label>
                    <select value={form.credential_type} onChange={e => setForm({ ...form, credential_type: e.target.value })} required className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="cred-type-select">
                      <option value="">Select type...</option>
                      {CREDENTIAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">License / ID Number</label>
                    <Input value={form.credential_number} onChange={e => setForm({ ...form, credential_number: e.target.value })} placeholder="e.g. RN-12345" className="h-10" data-testid="cred-number-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Issuing State</label>
                    <select value={form.issuing_state} onChange={e => setForm({ ...form, issuing_state: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="cred-state-select">
                      <option value="">Select state...</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Expiration Date *</label>
                    <Input type="date" value={form.expiration_date} onChange={e => setForm({ ...form, expiration_date: e.target.value })} required className="h-10" data-testid="cred-expiry-input" />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="submit" disabled={loading} className="bg-primary text-white rounded-full" data-testid="cred-submit-btn">
                    {loading ? 'Saving...' : 'Save Credential'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-full">Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {/* Credentials List */}
          {loading ? (
            <div className="text-center py-16 text-muted-foreground"><Loader2 className="h-6 w-6 text-primary mx-auto animate-spin" /></div>
          ) : credentials.length === 0 ? (
            <Card className="p-16 text-center border-dashed border-2 border-border/50">
              <Shield className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-foreground font-medium mb-1">No credentials yet</p>
              <p className="text-sm text-muted-foreground">Add your first license or certification above.</p>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="credentials-list">
              {credentials.map(cred => (
                <Card key={cred.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-4 border-border/50 hover:shadow-sm transition-all" data-testid={`credential-${cred.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <FileCheck className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="font-semibold text-foreground text-sm">{cred.credential_type}</h3>
                      <StatusBadge status={cred.status} />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {cred.credential_number && <span>#{cred.credential_number}</span>}
                      {cred.issuing_state && <span className="mx-1.5 text-border">|</span>}
                      {cred.issuing_state && <span>{cred.issuing_state}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Expires {new Date(cred.expiration_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(cred.id)} className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400 shrink-0" data-testid={`delete-cred-${cred.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CredentialTrackerPage;
